// =================================================================================
// File: src/hooks/useActiveStudioId.ts
// Description: Hook to get/set the active studio ID for the logged-in studio owner
// Reads from localStorage to persist studio selection across sessions
// =================================================================================

import { useState, useEffect } from "react";

const STORAGE_KEY = "mudo:activeStudioId";

/**
 * Hook to manage the active studio ID for studio owners
 * 
 * This hook:
 * - Reads the studio ID from localStorage on mount
 * - Provides a setter that persists to localStorage
 * - Returns the current studio ID and setter function
 * 
 * Usage:
 * ```tsx
 * const { studioId, setStudioId } = useActiveStudioId();
 * 
 * if (!studioId) {
 *   return <div>Please set up your studio profile first</div>;
 * }
 * 
 * // Use studioId to query Supabase for this studio's data
 * const workshops = await listWorkshopsForStudio(studioId);
 * ```
 * 
 * @returns {Object} { studioId: string | null, setStudioId: (id: string | null) => void }
 */
export function useActiveStudioId() {
  const [studioId, setStudioIdState] = useState<string | null>(null);
  
  // Strict UUID pattern (v1–v5). Accepts canonical RFC-4122 forms only.
  const isUuid = (s: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      s.trim()
    );

  // Load from localStorage on mount
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    // Trim and strip accidental surrounding quotes
    const stored = raw.trim().replace(/^"|"$/g, "");

    if (isUuid(stored)) {
      setStudioIdState(stored);
    } else {
      // Auto-clean invalid cached values so UI can guide setup
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      setStudioIdState(null);
    }
  }, []);

  /**
   * Set the active studio ID and persist to localStorage
   * @param id - The studio ID to set (or null to clear)
   */
  const setStudioId = (id: string | null) => {
    if (id === null) {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      setStudioIdState(null);
      return;
    }

    const trimmed = id.trim().replace(/^"|"$/g, "");
    if (!isUuid(trimmed)) {
      // Ignore invalid writes to prevent the app getting stuck
      console.warn("Ignored non-UUID studioId:", id);
      return;
    }

    try { localStorage.setItem(STORAGE_KEY, trimmed); } catch {}
    setStudioIdState(trimmed);
  };

  return {
    studioId,
    setStudioId,
  };
}
